import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  AppStore,
  Effect,
  Instrument,
  ModuleType,
  PatternPreset,
  Track,
  TrackType,
  TransportState,
} from '../types'

const createId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const createSteps = (): { index: number; enabled: boolean; velocity: number; note: string; length: number }[] =>
  Array.from({ length: 16 }, (_, index) => ({ index, enabled: false, velocity: 100, note: 'C4', length: 1 }))

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const trackColor = (type: TrackType) => {
  switch (type) {
    case 'drum':
      return '#f4b447'
    case 'midi':
      return '#4c94e6'
    case 'audio':
      return '#7bc378'
    default:
      return '#118C9B'
  }
}

const initialTracks: Track[] = [
  { id: 'track-sub', name: 'Sub Pulse', type: 'synth', enabled: true, muted: false, soloed: false, volume: 0.82, pan: -0.15, instrumentId: 'inst-sub', color: trackColor('synth'), steps: createSteps(), active: true, params: { attack: 0.02, release: 0.2, depth: 0.45, freq: 0.5, reso: 0.2, wet: 0.1 } },
  { id: 'track-beatbox', name: 'Beatbox', type: 'drum', enabled: true, muted: false, soloed: false, volume: 0.9, pan: 0, instrumentId: 'inst-beatbox', color: trackColor('drum'), steps: createSteps().map((step, i) => ({ ...step, enabled: i % 2 === 1, velocity: 70 })), active: true, params: { attack: 0.01, release: 0.12, depth: 0.7, damp: 0.3, wet: 0.2 } },
  { id: 'track-bassline', name: 'Bassline', type: 'synth', enabled: true, muted: false, soloed: false, volume: 0.86, pan: 0.1, instrumentId: 'inst-bassline', color: trackColor('synth'), steps: createSteps().map((step, i) => ({ ...step, enabled: [0, 3, 6, 10, 12, 14].includes(i), velocity: 88 })), active: true, params: { attack: 0.02, release: 0.18, freq: 0.45, reso: 0.35, depth: 0.55 } },
  { id: 'track-pad', name: 'Pad Glow', type: 'midi', enabled: true, muted: false, soloed: false, volume: 0.64, pan: 0.25, instrumentId: 'inst-pad', color: trackColor('midi'), steps: createSteps(), active: true, params: { attack: 0.45, release: 0.75, width: 0.7, wet: 0.35, rate: 0.2 } },
]

const initialInstruments: Instrument[] = [
  { id: 'inst-sub', name: 'SubSynth', type: 'instrument', instrumentType: 'SubSynth', enabled: true, selected: true, parameters: { attack: 0.02, release: 0.2, depth: 0.45, freq: 0.5, reso: 0.2, wet: 0.1 } },
  { id: 'inst-beatbox', name: 'Beatbox', type: 'instrument', instrumentType: 'Beatbox', enabled: true, selected: false, parameters: { attack: 0.01, release: 0.12, depth: 0.7, damp: 0.3, wet: 0.2 } },
  { id: 'inst-pcm', name: 'PCMSynth', type: 'instrument', instrumentType: 'PCMSynth', enabled: true, selected: false, parameters: { attack: 0.05, release: 0.25, freq: 0.55, reso: 0.25, wet: 0.15 } },
  { id: 'inst-bassline', name: 'Bassline', type: 'instrument', instrumentType: 'Bassline', enabled: true, selected: false, parameters: { attack: 0.02, release: 0.18, freq: 0.45, reso: 0.35, depth: 0.55 } },
  { id: 'inst-pad', name: 'PadSynth', type: 'instrument', instrumentType: 'PadSynth', enabled: true, selected: false, parameters: { attack: 0.45, release: 0.75, width: 0.7, wet: 0.35, rate: 0.2 } },
  { id: 'inst-sbit', name: 'SbitSynth', type: 'instrument', instrumentType: 'SbitSynth', enabled: true, selected: false, parameters: { attack: 0.03, release: 0.16, freq: 0.6, reso: 0.4, depth: 0.65 } },
  { id: 'inst-modular', name: 'Modular', type: 'instrument', instrumentType: 'Modular', enabled: true, selected: false, parameters: { attack: 0.08, release: 0.3, rate: 0.4, depth: 0.5, freq: 0.62, reso: 0.35 } },
  { id: 'inst-organ', name: 'Organ', type: 'instrument', instrumentType: 'Organ', enabled: true, selected: false, parameters: { attack: 0.01, release: 0.2, wet: 0.12, width: 0.45, depth: 0.35 } },
  { id: 'inst-vocoder', name: 'Vocoder', type: 'instrument', instrumentType: 'Vocoder', enabled: true, selected: false, parameters: { attack: 0.04, release: 0.22, damp: 0.4, wet: 0.45, rate: 0.25 } },
  { id: 'inst-fm', name: 'FMSynth', type: 'instrument', instrumentType: 'FMSynth', enabled: true, selected: false, parameters: { attack: 0.03, release: 0.28, depth: 0.55, rate: 0.3, freq: 0.5, reso: 0.25 } },
]

const initialEffects: Effect[] = [
  { id: 'fx-reverb', name: 'Space Reverb', type: 'effect', effectType: 'reverb', enabled: true, selected: false, parameters: { wet: 0.22, damp: 0.35, depth: 0.4 } },
  { id: 'fx-delay', name: 'Tape Delay', type: 'effect', effectType: 'delay', enabled: false, selected: false, parameters: { delay: 0.35, wet: 0.18, feedback: 0.3 } },
  { id: 'fx-filter', name: 'Reso Filter', type: 'effect', effectType: 'filter', enabled: true, selected: false, parameters: { freq: 0.62, reso: 0.35, damp: 0.2 } },
  { id: 'fx-distortion', name: 'Bit Crush', type: 'effect', effectType: 'distortion', enabled: false, selected: false, parameters: { depth: 0.5, damp: 0.25, wet: 0.2 } },
  { id: 'fx-chorus', name: 'Motion Chorus', type: 'effect', effectType: 'chorus', enabled: false, selected: false, parameters: { rate: 0.28, depth: 0.35, width: 0.65, wet: 0.2 } },
  { id: 'fx-limiter', name: 'Master Limiter', type: 'effect', effectType: 'limiter', enabled: true, selected: false, parameters: { threshold: 0.82, ratio: 0.9 } },
]

const initialTransport: TransportState = {
  isPlaying: false,
  isRecording: false,
  tempo: 120,
  timeSignature: [4, 4],
  position: 0,
  loop: true,
  masterVolume: 0.9,
  masterPan: 0,
  limiter: true,
}

export const useStore = create<AppStore>()(
  persist(
    (set, get) => ({
      tracks: initialTracks,
      modules: [...initialInstruments, ...initialEffects],
      patches: [],
      transport: initialTransport,
      selectedTrackId: 'track-sub',
      selectedModuleId: 'inst-sub',
      selectedModuleType: 'instrument',
      patternName: 'Clear',

      addTrack: () => {
        const state = get()
        const newTrack: Track = {
          id: createId('track'),
          name: `Track ${state.tracks.length + 1}`,
          type: 'synth',
          enabled: true,
          muted: false,
          soloed: false,
          volume: 0.8,
          pan: 0,
          instrumentId: state.modules.find((m) => m.type === 'instrument')?.id || '',
          color: trackColor('synth'),
          steps: createSteps(),
          active: true,
          params: { attack: 0.05, release: 0.25, depth: 0.45, rate: 0.2, freq: 0.5, reso: 0.25, damp: 0.2, wet: 0.15 },
        }
        set((current) => ({
          tracks: [...current.tracks, newTrack],
          selectedTrackId: newTrack.id,
        }))
      },

      removeTrack: (id: string) => {
        const state = get()
        const nextTracks = state.tracks.filter((track) => track.id !== id)
        set({
          tracks: nextTracks,
          selectedTrackId: nextTracks[0]?.id || null,
        })
      },

      updateTrack: (id: string, updates: Partial<Track>) => {
        set((state) => ({
          tracks: state.tracks.map((track) => (track.id === id ? { ...track, ...updates } : track)),
        }))
      },

      toggleStep: (trackId: string, stepIndex: number) => {
        set((state) => ({
          tracks: state.tracks.map((track) => {
            if (track.id !== trackId) return track
            const nextSteps = track.steps.map((step) =>
              step.index === stepIndex ? { ...step, enabled: !step.enabled } : step,
            )
            return { ...track, steps: nextSteps }
          }),
        }))
      },

      setStepVelocity: (trackId: string, stepIndex: number, velocity: number) => {
        set((state) => ({
          tracks: state.tracks.map((track) => {
            if (track.id !== trackId) return track
            const nextVelocity = clamp(Math.round(velocity), 0, 100)
            const nextSteps = track.steps.map((step) =>
              step.index === stepIndex ? { ...step, velocity: nextVelocity } : step,
            )
            return { ...track, steps: nextSteps }
          }),
        }))
      },

      clearSequence: (trackId: string) => {
        set((state) => ({
          tracks: state.tracks.map((track) => (track.id !== trackId ? track : { ...track, steps: createSteps() })),
          patternName: 'Clear',
        }))
      },

      randomizeSequence: (trackId: string) => {
        set((state) => ({
          tracks: state.tracks.map((track) => {
            if (track.id !== trackId) return track
            const nextSteps = createSteps().map((step) => ({
              ...step,
              enabled: Math.random() > 0.62,
              velocity: Math.round(45 + Math.random() * 55),
            }))
            return { ...track, steps: nextSteps }
          }),
          patternName: 'Random',
        }))
      },

      applyPreset: (trackId: string, preset: PatternPreset) => {
        set((state) => ({
          tracks: state.tracks.map((track) => {
            if (track.id !== trackId) return track
            const steps = createSteps()
            if (preset === 'clear') {
              return { ...track, steps, patternName: 'Clear' }
            }
            if (preset === 'random') {
              return {
                ...track,
                steps: steps.map((step) => ({
                  ...step,
                  enabled: Math.random() > 0.62,
                  velocity: Math.round(45 + Math.random() * 55),
                })),
                patternName: 'Random',
              }
            }
            if (preset === 'kick') {
              [0, 4, 8, 12, 2, 10].forEach((stepIndex) => {
                steps[stepIndex].enabled = true
                steps[stepIndex].velocity = stepIndex % 4 === 0 ? 100 : 74
              })
            }
            if (preset === 'hat') {
              [1, 3, 5, 7, 9, 11, 13, 15].forEach((stepIndex) => {
                steps[stepIndex].enabled = true
                steps[stepIndex].velocity = 62
              })
            }
            if (preset === 'bass') {
              [0, 3, 6, 10, 12, 14].forEach((stepIndex) => {
                steps[stepIndex].enabled = true
                steps[stepIndex].velocity = 88
              })
            }
            return { ...track, steps, patternName: preset }
          }),
        }))
      },

      toggleTransport: () => {
        set((state) => ({
          transport: { ...state.transport, isPlaying: !state.transport.isPlaying },
        }))
      },

      toggleRecord: () => {
        set((state) => ({
          transport: { ...state.transport, isRecording: !state.transport.isRecording },
        }))
      },

      stopTransport: () => {
        set((state) => ({
          transport: { ...state.transport, isPlaying: false, position: 0 },
        }))
      },

      setTempo: (tempo: number) => {
        const nextTempo = clamp(Math.round(tempo), 40, 300)
        set((state) => ({ transport: { ...state.transport, tempo: nextTempo } }))
      },

      setPosition: (position: number) => {
        set((state) => {
          const maxBar = state.transport.timeSignature[0] * 4
          const nextPosition = ((position % maxBar) + maxBar) % maxBar
          return { transport: { ...state.transport, position: nextPosition } }
        })
      },

      setLoop: (loop: boolean) => {
        set((state) => ({ transport: { ...state.transport, loop } }))
      },

      updateModuleParameter: (moduleType: ModuleType, moduleId: string, key: string, value: number) => {
        set((state) => ({
          modules: state.modules.map((mod) =>
            mod.id === moduleId && mod.type === moduleType ? { ...mod, parameters: { ...mod.parameters, [key]: value } } : mod,
          ),
        }))
      },

      toggleModule: (id: string) => {
        set((state) => ({
          modules: state.modules.map((mod) => (mod.id === id ? { ...mod, enabled: !mod.enabled } : mod)),
        }))
      },

      addPatch: (from, to) => {
        if (from === to) return
        set((state) => {
          const exists = state.patches.some((patch) => patch.from === from && patch.to === to)
          if (exists) return state
          return { patches: [...state.patches, { id: createId('patch'), from, to, color: '#ffd700' }] }
        })
      },

      clearPatches: () => set({ patches: [] }),

      updateMaster: (updates) => {
        set((state) => ({
          transport: {
            ...state.transport,
            masterVolume: updates.volume ?? state.transport.masterVolume,
            masterPan: updates.pan ?? state.transport.masterPan,
            limiter: updates.limiter ?? state.transport.limiter,
          },
        }))
      },

      selectTrack: (id: string) => set({ selectedTrackId: id }),

      selectModule: (moduleType: ModuleType, moduleId: string) => {
        set((state) => ({
          selectedModuleId: moduleId,
          selectedModuleType: moduleType,
          modules: state.modules.map((mod) => ({ ...mod, selected: mod.id === moduleId })),
        }))
      },
    }),
    {
      name: 'cccc-daw-state',
      partialize: (state) => ({
        tracks: state.tracks,
        modules: state.modules,
        patches: state.patches,
        transport: { ...state.transport, isPlaying: false, isRecording: false, position: 0 },
        selectedTrackId: state.selectedTrackId,
        selectedModuleId: state.selectedModuleId,
        selectedModuleType: state.selectedModuleType,
        patternName: state.patternName,
      }),
    },
  ),
)
